import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, TrendingUp, DollarSign, Eye } from "lucide-react";

interface CPMCalculatorProps {
  open: boolean;
  onClose: () => void;
}

const CPMCalculator: React.FC<CPMCalculatorProps> = ({ open, onClose }) => {
  const [values, setValues] = useState({
    totalCost: "",
    impressions: "",
    clicks: "",
    conversions: ""
  });

  const [results, setResults] = useState({
    cpm: 0,
    cpc: 0,
    ctr: 0,
    cpa: 0,
    reach: 0
  });

  const handleInputChange = (field: string, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    calculateMetrics({ ...values, [field]: value });
  };

  const calculateMetrics = (inputs: typeof values) => {
    const cost = parseFloat(inputs.totalCost) || 0;
    const impressions = parseFloat(inputs.impressions) || 0;
    const clicks = parseFloat(inputs.clicks) || 0;
    const conversions = parseFloat(inputs.conversions) || 0;

    // CPM = (Total Cost / Impressions) * 1000
    const cpm = impressions > 0 ? (cost / impressions) * 1000 : 0;
    
    // CPC = Total Cost / Clicks
    const cpc = clicks > 0 ? cost / clicks : 0;
    
    // CTR = (Clicks / Impressions) * 100
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    
    // CPA = Total Cost / Conversions
    const cpa = conversions > 0 ? cost / conversions : 0;
    
    // Estimated reach (assuming 3 impressions per unique user)
    const reach = impressions > 0 ? Math.round(impressions / 3) : 0;

    setResults({
      cpm,
      cpc,
      ctr,
      cpa,
      reach
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const getPerformanceIndicator = (ctr: number) => {
    if (ctr > 2) return { text: "Excellent CTR", color: "text-green-600" };
    if (ctr > 1) return { text: "Good CTR", color: "text-blue-600" };
    if (ctr > 0.5) return { text: "Average CTR", color: "text-yellow-600" };
    return { text: "Below Average CTR", color: "text-red-600" };
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            CPM Calculator
          </DialogTitle>
          <DialogDescription>
            Calculate cost per thousand impressions and media planning metrics
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Metrics</CardTitle>
              <CardDescription>Enter your campaign cost and performance data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="totalCost">Total Campaign Cost ($)</Label>
                <Input
                  id="totalCost"
                  type="number"
                  placeholder="5000"
                  value={values.totalCost}
                  onChange={(e) => handleInputChange('totalCost', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="impressions">Total Impressions</Label>
                <Input
                  id="impressions"
                  type="number"
                  placeholder="100000"
                  value={values.impressions}
                  onChange={(e) => handleInputChange('impressions', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="clicks">Total Clicks (Optional)</Label>
                <Input
                  id="clicks"
                  type="number"
                  placeholder="1500"
                  value={values.clicks}
                  onChange={(e) => handleInputChange('clicks', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="conversions">Conversions (Optional)</Label>
                <Input
                  id="conversions"
                  type="number"
                  placeholder="75"
                  value={values.conversions}
                  onChange={(e) => handleInputChange('conversions', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card>
            <CardHeader>
              <CardTitle>Media Planning Metrics</CardTitle>
              <CardDescription>Your calculated cost and performance metrics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold">CPM (Cost Per Mille)</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(results.cpm)}
                  </p>
                  <p className="text-sm text-gray-600">Per 1,000 impressions</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold">CPC (Cost Per Click)</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(results.cpc)}
                  </p>
                  <p className="text-sm text-gray-600">Per click</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    <h4 className="font-semibold">CTR (Click-Through Rate)</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {results.ctr.toFixed(2)}%
                  </p>
                  <p className="text-sm text-gray-600">Click rate</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-orange-600" />
                    <h4 className="font-semibold">CPA (Cost Per Acquisition)</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(results.cpa)}
                  </p>
                  <p className="text-sm text-gray-600">Per conversion</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="h-5 w-5 text-indigo-600" />
                    <h4 className="font-semibold">Estimated Reach</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(results.reach)}
                  </p>
                  <p className="text-sm text-gray-600">Unique users</p>
                </div>
              </div>

              {/* Performance Analysis */}
              {values.clicks && values.impressions && (
                <div className="mt-6 p-4 rounded-lg bg-blue-50">
                  <h4 className="font-semibold text-blue-900 mb-2">Performance Analysis</h4>
                  <div className="text-sm text-blue-800">
                    <p className={getPerformanceIndicator(results.ctr).color}>
                      {getPerformanceIndicator(results.ctr).text}
                    </p>
                    <p className="mt-1">
                      Industry average CTR is typically 1-2% for display ads.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => {
            setValues({
              totalCost: "",
              impressions: "",
              clicks: "",
              conversions: ""
            });
            setResults({
              cpm: 0,
              cpc: 0,
              ctr: 0,
              cpa: 0,
              reach: 0
            });
          }}>
            Reset Calculator
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CPMCalculator;